import React from 'react';
import { Link } from 'react-router-dom';

import {
  AlertTriangle,
  Checks,
  ExternalLink,
  InfoCircle,
} from '@saleshandy/icons';
import { supportUrls } from '../../utils/urls';
import {
  CustomDomainScore,
  DailySendingQuotaScore,
  DkimScore,
  DmarcScore,
  DomainBlacklistScore,
  EmailAgeScore,
  IPBlacklistScore,
  MXRecordsScore,
  SendingIntervalScore,
  SpfScore,
} from './enums';
import { isPtrStatusNotValid } from '../../../components/email-accounts/enums/email-account';
import Alert from '../../design-system/components/alert';
import { getBlacklistedCount } from './helper';
import {
  OverlayTooltip,
  Placement,
} from '../../design-system/components/overlay';
import { Images } from '../../app-constants';
import { accessibleOnClick } from '../../utils/accessible-on-click';
import S3StaticImage from '../../utils/s3-static-images';

export const getSPFDescription = (score: number) =>
  score === SpfScore.min ? (
    <p>
      Uh-oh! The SPF record is not set for this email account. It's critically
      important, so we recommend urgently setting it up, as mentioned{' '}
      <a href={supportUrls.spfBlog} target="_blank" rel="noreferrer">
        here
      </a>
      .
    </p>
  ) : (
    <p>Great news! Your SPF record is set up correctly.</p>
  );

export const getCTDDescription = (score: number) =>
  score === CustomDomainScore.min ? (
    <p>
      Oops! The custom tracking domain is not set.{' '}
      <a href={supportUrls.ctdBLog} target="_blank" rel="noreferrer">
        Learn how
      </a>{' '}
      to set up a custom tracking domain & set it up{' '}
      <Link to="/settings/custom-domain">here</Link>.
    </p>
  ) : (
    <p>Congratulations! Your custom tracking domain is set up correctly.</p>
  );

export const getDSQDescription = (score: number) => {
  if (score === DailySendingQuotaScore.min) {
    return (
      <p>
        Your email account has a high daily sending quota, which can increase
        the risk of hitting spam filters. We strongly recommend it to keep below
        50.
      </p>
    );
  }

  if (score === DailySendingQuotaScore.mid) {
    return (
      <p>
        Your email account has a moderate daily sending quota. It's important to
        follow proper email-sending practices to avoid hitting your quota and
        maintain a good sender reputation. Consider segmenting your email list
        and sending emails in batches to stay within your sending quota.
      </p>
    );
  }

  return (
    <p>
      Excellent! Your email account has an ideal daily sending quota, a good
      practice to prevent spam issues.
    </p>
  );
};

export const getDMARCDescription = (score: number) =>
  score === DmarcScore.min ? (
    <p>
      The DMARC record is not set. It helps prevent email spoofing, so we
      suggest setting it up, as mentioned{' '}
      <a href={supportUrls.dmarcBlog} target="_blank" rel="noreferrer">
        here
      </a>
      .
    </p>
  ) : (
    <p>Excellent! Your DMARC record is set up correctly.</p>
  );

export const getDkimDescription = (score: number) =>
  score && score === DkimScore.max ? (
    <p>Excellent! Your DKIM record is set up correctly.</p>
  ) : (
    <p>
      DKIM is not set up. It ensures your emails are secure and trusted. Set it
      up as mentioned{' '}
      <a href={supportUrls.dkimBlog} target="_blank" rel="noreferrer">
        here
      </a>
      .
    </p>
  );

type PTRDescriptionMessages = {
  notValidDescription: string;
  validDescription: string;
};

export const getPTRDescription = (
  ptr: { status: string } | null | undefined,
  messages?: PTRDescriptionMessages,
): React.ReactNode => {
  const notValidText =
    messages?.notValidDescription ||
    'PTR record is not set for your sending IP. This may impact email deliverability. Please contact your domain provider to configure it.';
  const validText =
    messages?.validDescription ||
    'Your PTR (Reverse DNS) record is set up correctly. This helps improve email trust and inbox placement.';
  if (isPtrStatusNotValid(ptr?.status)) {
    return <p>{notValidText}</p>;
  }
  return <p>{validText}</p>;
};

export const getDBLDescription = (
  score: number,
  isFreeDomainOrIp = false,
  dataLength = 0,
) => {
  if (isFreeDomainOrIp) {
    return (
      <p>
        Free domains are excluded from domain blacklist checks as they pose
        minimal risk.
      </p>
    );
  }

  if (score === DomainBlacklistScore.min && dataLength === 0) {
    return <p>We couldn't complete the blacklist check.</p>;
  }

  if (score && score === DomainBlacklistScore.max) {
    return <p>Excellent! Your Domain was not found blacklisted.</p>;
  }
  return (
    <p>
      This domain was found blacklisted it may cause your emails to land in
      spam, to know how to delist the domain{' '}
      <a
        href={supportUrls.domainAndIPBlacklistBlog}
        target="_blank"
        rel="noreferrer"
      >
        click here
      </a>
    </p>
  );
};

export const getIPBLDescription = (
  score: number,
  isFreeDomainOrIp = false,
  dataLength = 0,
) => {
  if (isFreeDomainOrIp) {
    return (
      <p>
        Free domains are excluded from IP blacklist checks as they pose minimal
        risk.
      </p>
    );
  }

  if (score === IPBlacklistScore.min && dataLength === 0) {
    return (
      <p>
        We couldn't complete the blacklist check as the IP of the domain was not
        found.
      </p>
    );
  }

  if (score && score === IPBlacklistScore.max) {
    return <p>Excellent! Your IP was not found blacklisted.</p>;
  }

  return (
    <p>
      This IP was found blacklisted it may cause your emails to land in spam, to
      know how to delist IPs{' '}
      <a
        href={supportUrls.domainAndIPBlacklistBlog}
        target="_blank"
        rel="noreferrer"
      >
        click here
      </a>
    </p>
  );
};

export const getMXRecordsDescription = (score: number) =>
  score && score === MXRecordsScore.max ? (
    <p>Excellent! Your MX record is set up correctly.</p>
  ) : (
    <p>
      MX record is missing. It's essential for receiving emails. Set it up as
      mentioned{' '}
      <a href={supportUrls.mxRecordBlog} target="_blank" rel="noreferrer">
        here.
      </a>
      .
    </p>
  );

export const getEmailAgeDescription = (score: number) => {
  if (score === EmailAgeScore.min) {
    return (
      <p>
        This email account is less than 3 months old. Please send fewer emails,
        enable ramp-up & use email warm-up for this email account. To know more{' '}
        <a href={supportUrls.emailAgeBlog} target="_blank" rel="noreferrer">
          click here
        </a>
      </p>
    );
  }

  if (score === EmailAgeScore.mid) {
    return (
      <p>
        This email account is between 3-6 months old. It's still relatively new,
        but with proper email sending practices, you can improve your sender
        reputation and email deliverability. We recommend you to keep the daily
        sending quota between 25-75 to ensure your email account is not flagged
        for spam. We also suggest you to use email warm-up to maintain a optimum
        deliverability. To know more{' '}
        <a href={supportUrls.emailAgeBlog} target="_blank" rel="noreferrer">
          click here
        </a>
      </p>
    );
  }

  return <p>Great! The email account is more than 6 months old.</p>;
};

export const getSendingIntervalDescription = (score: number) => {
  if (score === SendingIntervalScore.min) {
    return (
      <p>
        Uh-oh! The time interval between two emails is too short. It can affect
        the deliverability; therefore, we suggest you increase it to 200
        seconds. To know more{' '}
        <a
          href={supportUrls.emailSendingIntervalBlog}
          target="_blank"
          rel="noreferrer"
        >
          click here
        </a>
      </p>
    );
  }

  if (score === SendingIntervalScore.mid) {
    return (
      <p>
        Your email account is sending emails at a moderate pace, but it's still
        important to follow proper email-sending practices to maintain a good
        sender reputation. Consider increasing the interval beyond 180 seconds
        to further improve deliverability and prevent spam issues. To know more{' '}
        <a
          href={supportUrls.emailSendingIntervalBlog}
          target="_blank"
          rel="noreferrer"
        >
          click here
        </a>
      </p>
    );
  }

  return (
    <p>
      Excellent! The email-sending speed is reasonable, which can help prevent
      spam issues.
    </p>
  );
};

export const getDomainBlacklistContent = (domainBlacklist: {
  domain: string;
  ip: string;
  data: { status: string; blacklist: string[] }[];
}) => {
  const blacklistedCount = getBlacklistedCount(domainBlacklist?.data);
  return (
    <>
      <span className="modal-content-title">
        Your domain{' '}
        <span className="modal-content-title-domain">
          {domainBlacklist.domain}
        </span>{' '}
        IP address is{' '}
        <span className="modal-content-title-ip">{domainBlacklist.ip}</span>{' '}
      </span>
      {blacklistedCount > 0 ? (
        <Alert
          variant={Alert.Variant.Danger}
          className="modal-content-alert-danger"
          theme={Alert.Theme.New}
          contentContainer={
            <>
              <span>
                <AlertTriangle />
              </span>
              <div className="modal-content-alert-content">
                <span className="modal-content-alert-title">
                  {blacklistedCount} Listed - You are listed on{' '}
                  {blacklistedCount} blacklists.
                </span>
                <span className="modal-content-alert-sub-title">
                  We checked {domainBlacklist.domain} across 100+ blacklists, If
                  domain is blacklisted then it'll impact your deliverability.
                  <OverlayTooltip
                    className="permission-popover"
                    placement={Placement.Bottom}
                    text="If your domain is newly purchased, it might be on one of the blacklists. Please wait for 4-5 weeks. If it's still listed, follow the steps to delist it."
                  >
                    <span>
                      <InfoCircle className="info-icon-popover-btn cursor-pointer" />
                    </span>
                  </OverlayTooltip>
                </span>
              </div>
            </>
          }
        />
      ) : (
        <Alert
          variant={Alert.Variant.Success}
          className="modal-content-alert-success"
          theme={Alert.Theme.New}
          contentContainer={
            <>
              <Checks color="#059669" />
              <div className="modal-content-alert-content">
                <span className="modal-content-alert-title">
                  100% Pass - You are not on any blacklists!
                </span>
                <span className="modal-content-alert-sub-title">
                  We checked {domainBlacklist?.domain} across all known
                  blacklists using our blacklist checker.
                </span>
              </div>
            </>
          }
        />
      )}
    </>
  );
};

export const getIPBlacklistContent = (ipBlacklist: {
  ip: string;
  data: { status: string; blacklist: string[] }[];
}) => {
  const blacklistedCount = getBlacklistedCount(ipBlacklist?.data);
  return (
    <>
      <span className="modal-content-title">
        Your Email sending IP is{' '}
        <span className="modal-content-title-ip">{ipBlacklist?.ip}</span>
      </span>
      {blacklistedCount > 0 ? (
        <Alert
          variant={Alert.Variant.Danger}
          className="modal-content-alert-danger"
          theme={Alert.Theme.New}
          contentContainer={
            <>
              <span>
                <AlertTriangle />
              </span>
              <div className="modal-content-alert-content">
                <span className="modal-content-alert-title">
                  {blacklistedCount} Listed - You are listed on{' '}
                  {blacklistedCount} blacklists.
                </span>
                <span className="modal-content-alert-sub-title">
                  We checked {ipBlacklist?.ip} across 100+ blacklists, if IP is
                  blacklisted then it'll impact your email deliverability.
                </span>
              </div>
            </>
          }
        />
      ) : (
        <Alert
          variant={Alert.Variant.Success}
          className="modal-content-alert-success"
          theme={Alert.Theme.New}
          contentContainer={
            <>
              <Checks color="#059669" />
              <div className="modal-content-alert-content">
                <span className="modal-content-alert-title">
                  100% Pass - You are not on any blacklists!
                </span>
                <span className="modal-content-alert-sub-title">
                  We checked {ipBlacklist?.ip} across all known blacklists using
                  our blacklist checker.
                </span>
              </div>
            </>
          }
        />
      )}
    </>
  );
};

export const getCopyContent = (content: string) => (
  <div className="copy-container">
    <div className="copy-content">
      <code>{content}</code>
    </div>
  </div>
);

export const getPtrSummaryString = (ip: string, domain: string): string =>
  ip && domain ? `${ip} points to ${domain}, ${domain} points back to ${ip}` : ip || '';

export const getLearnMoreContent = (type: string, url: string) => (
  <div
    className="modal-learn-more"
    {...accessibleOnClick(() => window.open(url, '_blank'))}
  >
    <span>{`Learn how to set up a ${type} record`}</span>
    <ExternalLink />
  </div>
);

export const getDomainBlacklistFooter = (onViewYoutubeVideo?: () => void) => (
  <div className="modal-content-footer">
    <span className="blacklist-footer-title">
      What to do after being blacklisted?
      <S3StaticImage
        imagePath="youtube.svg"
        {...accessibleOnClick(onViewYoutubeVideo)}
        className="ml-2"
        alt="youtube"
      />
    </span>
    <span className="blacklist-footer-content">
      <span className="blacklist-footer-content-title">Request Removal: </span>
      Contact the blacklists where your domain is listed and follow their
      instructions to remove it.{' '}
      <a
        href={supportUrls.domainAndIPBlacklistBlog}
        target="_blank"
        rel="noreferrer"
      >
        Learn more
      </a>
    </span>
    <span className="blacklist-footer-content">
      <span className="blacklist-footer-content-title">
        Check Your Deliverability:{' '}
      </span>
      If you notice a sharp decline in email deliverability, it might be a good
      idea to switch to a new domain.
    </span>
  </div>
);

export const getIPBlacklistFooter = (onViewYoutubeVideo?: () => void) => (
  <div className="modal-content-footer">
    <span className="blacklist-footer-title">
      What to do after being blacklisted?
      <S3StaticImage
        imagePath="youtube.svg"
        {...accessibleOnClick(onViewYoutubeVideo)}
        alt="youtube"
        className="ml-2"
      />
    </span>
    <span className="blacklist-footer-content">
      <span className="blacklist-footer-content-title">Contact ESP: </span>
      Consult your ESP and report them about low reputed IP addresses attached
      in sending emails.{' '}
      <a
        href={supportUrls.domainAndIPBlacklistBlog}
        target="_blank"
        rel="noreferrer"
      >
        Learn more
      </a>
    </span>
    <span className="blacklist-footer-content">
      <span className="blacklist-footer-content-title">
        Check Your Deliverability:{' '}
      </span>
      If you notice a sharp decline in email deliverability, it might be a good
      idea to switch to a new domain.
    </span>
  </div>
);
